import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, XIcon } from 'lucide-react';
import type { Dealer } from '../types/dealers.types';

interface Props {
    data: Dealer[];
    isLoading?: boolean;
    onDetach: (dealerId: string) => void;
    detachingId?: string | null;
    emptyMessage?: string;
}

/**
 * Shared list rendering for the OemsTab / FranchisesTab franchise-partner
 * rows. DealerDataTable has no per-row Detach slot, so this renders the
 * shared Table<Dealer> directly (as DealerDataTable itself does) with a
 * small franchise-specific column set: name, dealer_code, city, and a
 * guarded Detach action.
 */
export const FranchiseMembersTable: React.FC<Props> = ({ data, isLoading = false, onDetach, detachingId = null, emptyMessage }) => {
    const navigate = useNavigate();

    const columns = useMemo<ColumnDef<Dealer>[]>(() => [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'dealer_code',
            header: 'Code',
            cell: (info) => <span className="font-mono text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'city',
            header: 'City',
            cell: (info) => <span className="text-sm">{info.getValue() as string}</span>,
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => {
                const dealer = info.row.original;
                return (
                    <div className="flex space-x-2">
                        <Tooltip content="View dealer">
                            <TextLink
                                onClick={() => navigate(`/admin/dealers/${dealer.id}`)}
                                color="primary"
                                ariaLabel={`View ${dealer.name}`}
                                startIcon={<EyeIcon size={20} />}
                            />
                        </Tooltip>
                        <PermissionGuard permission={{ resource: 'dealer', action: 'edit' }}>
                            <Tooltip content="Detach">
                                <TextLink
                                    onClick={() => onDetach(dealer.id)}
                                    disabled={detachingId === dealer.id}
                                    color="error"
                                    ariaLabel={`Detach ${dealer.name}`}
                                    startIcon={<XIcon size={18} />}
                                />
                            </Tooltip>
                        </PermissionGuard>
                    </div>
                );
            },
        },
    ], [navigate, onDetach, detachingId]);

    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading..." />;
    }

    return <Table<Dealer> data={data} columns={columns} enableSorting={false} emptyMessage={emptyMessage} />;
};
